import { API_PATHS } from '@/constants/api-paths'
import { api } from '@/lib/api'

type ListResponse<T> = {
  results?: T[]
  data?: { results?: T[] } | T[]
}

type WrappedResponse<T> = {
  data?: T
}

export type AdmsAttendanceDevice = {
  id: string | number
  device_sn: string
  name?: string | null
  location_id?: string | number | null
  location_name?: string | null
  attendance_mode?: 'auto_toggle' | 'device_state' | null
  active?: boolean
  last_seen_at?: string | null
  org_instance?: string | number | null
  org_instance_id?: string | number | null
}

export type AdmsWorkerPinMapping = {
  id: string | number
  device?: string | number
  device_sn: string
  pin: string
  worker: string | number
  worker_name?: string | null
  active?: boolean
  org_instance?: string | number | null
  org_instance_id?: string | number | null
}

export type RegisterAdmsDevicePayload = {
  service_instance_id: string | number
  device_sn: string
  name?: string
  attendance_mode?: 'auto_toggle' | 'device_state'
  active?: boolean
}

export type UpsertAdmsMappingPayload = {
  device_sn: string
  pin: string
  worker_id: string | number
  active?: boolean
}

const unwrapList = <T>(payload: ListResponse<T> | T[]): T[] => {
  if (Array.isArray(payload)) return payload
  if (Array.isArray(payload.results)) return payload.results
  if (Array.isArray(payload.data)) return payload.data
  if (payload.data && !Array.isArray(payload.data) && Array.isArray(payload.data.results)) {
    return payload.data.results
  }
  return []
}

export const admsDevicesApi = {
  listDevices: async (serviceInstanceId: string | number): Promise<AdmsAttendanceDevice[]> => {
    const { data } = await api.get<ListResponse<AdmsAttendanceDevice>>(API_PATHS.workers.admsDeviceList, {
      params: { service_instance_id: serviceInstanceId },
    })
    return unwrapList(data)
  },

  registerDevice: async (payload: RegisterAdmsDevicePayload): Promise<AdmsAttendanceDevice> => {
    const { data } = await api.post<WrappedResponse<AdmsAttendanceDevice> & { device?: AdmsAttendanceDevice }>(
      API_PATHS.workers.admsDeviceRegister,
      payload,
    )
    return data.device ?? data.data!
  },

  updateDevice: async (payload: { service_instance_id: string | number; id: string | number; active?: boolean; remove?: boolean }): Promise<AdmsAttendanceDevice | null> => {
    const { data } = await api.post<WrappedResponse<AdmsAttendanceDevice> & { device?: AdmsAttendanceDevice; removed?: boolean }>(
      API_PATHS.workers.admsDeviceUpdate,
      payload,
    )
    if (data.removed) return null
    return data.device ?? data.data ?? null
  },

  listMappings: async (serviceInstanceId: string | number, deviceSn?: string): Promise<AdmsWorkerPinMapping[]> => {
    const { data } = await api.get<ListResponse<AdmsWorkerPinMapping>>(API_PATHS.workers.admsMappingList, {
      params: {
        service_instance_id: serviceInstanceId,
        device_sn: deviceSn || undefined,
      },
    })
    return unwrapList(data)
  },

  upsertMapping: async (payload: UpsertAdmsMappingPayload): Promise<AdmsWorkerPinMapping> => {
    const { data } = await api.post<WrappedResponse<AdmsWorkerPinMapping> & { mapping?: AdmsWorkerPinMapping }>(
      API_PATHS.workers.admsMappingUpsert,
      payload,
    )
    return data.mapping ?? data.data!
  },
}

export default admsDevicesApi
